/** In-app modal sheet (bottom sheet on phones, centered dialog on wide screens). No native confirm(). */
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { IconClose } from './Icons';

export function Sheet({
  open,
  onClose,
  title,
  children,
  wide = false,
  className = '',
  labelledBy,
}: {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  children: ReactNode;
  wide?: boolean;
  className?: string;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const t = setTimeout(() => {
      const el = ref.current?.querySelector<HTMLElement>('[data-autofocus], button, [href], input, select');
      el?.focus({ preventScroll: true });
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.({ preventScroll: true });
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="sheet-backdrop" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        ref={ref}
        className={`sheet panel${wide ? ' wide' : ''} ${className}`}
        role="dialog"
        aria-modal="true"
        {...(labelledBy ? { 'aria-labelledby': labelledBy } : {})}
      >
        {(title || onClose) && (
          <div className="sheet-head">
            {title && (
              <h2 className="sheet-title" id={labelledBy}>
                {title}
              </h2>
            )}
            {onClose && (
              <button className="sheet-close icon-btn" onClick={onClose} aria-label="Close">
                <IconClose size={20} />
              </button>
            )}
          </div>
        )}
        <div className="sheet-body">{children}</div>
      </div>
    </div>
  );
}
